import React, { useState } from "react"

import { Analytics } from "@bounce/analytics"
import { Button } from "@bounce/design-system"
import { useTranslation } from "@bounce/i18n"
import { App, useAsync } from "@bounce/util"
import type { NavbarBrandProps } from "@bounce/web-components"
import { Navbar as BaseNavbar, NavbarBrand, NavbarBurgerButton } from "@bounce/web-components"

import { NavbarDesktop } from "./NavbarDesktop"
import { NavbarMobile } from "./NavbarMobile"
import type { MobileAndDesktopProps } from "./types"
import SafeLink from "@/components/Generic/SafeLink/SafeLink"
import { useAppStoreUrl } from "@/hooks/useAppStoreUrl"
import { InteractionEvents } from "@/libs/analytics/events"
import { useUrl } from "@/libs/router"
import type { Product } from "@/types"
import { useAppURL } from "@/utils/appURL"
import { getUser } from "@/utils/auth"

export type NavbarProps = {
  product: Product
  Logo: NavbarBrandProps["Logo"]
  hideBurgerButton?: boolean
} & Omit<MobileAndDesktopProps, "Logo">

export const Navbar = ({
  alternates,
  Logo,
  links,
  languages,
  product,
  className,
  hideBurgerButton,
  onToggleMenu,
}: NavbarProps) => {
  const [showMenu, setShowMenu] = useState(false)
  const { data: user } = useAsync(getUser)
  const authenticated = !!user

  const onClickBurger = () => {
    const menuWillBeOpen = !showMenu

    Analytics.track(InteractionEvents.MenuBurguerClicked, { menuWillBeOpen })
    setShowMenu(menuWillBeOpen)
    onToggleMenu?.(menuWillBeOpen)
  }

  return (
    <BaseNavbar className={className}>
      <NavbarBrand
        Logo={Logo}
        title="Bounce"
        className={showMenu ? "text-white" : "text-primary"}
        {...links.homepage}
      />
      <NavbarDesktop
        links={links}
        alternates={alternates}
        authenticated={authenticated}
        languages={languages}
        product={product}
        className="hidden xl:flex"
      />
      {showMenu && (
        <NavbarMobile links={links} authenticated={authenticated} languages={languages} className="flex xl:hidden" />
      )}
      <NavBarButton hideBurgerButton={hideBurgerButton} showMenu={showMenu} onClickBurger={onClickBurger} />
    </BaseNavbar>
  )
}

const NavBarButton = ({
  hideBurgerButton,
  showMenu,
  onClickBurger,
}: {
  hideBurgerButton?: boolean
  showMenu: boolean
  onClickBurger: () => void
}) => {
  const buildAppUrl = useAppURL()
  const { getUrl } = useUrl()
  const eventPropsName = hideBurgerButton ? "AppNavBar" : "AppBannerLuggageStorageTertiary"
  const { href, onClick } = useAppStoreUrl({
    app: App.Customer,
    path: buildAppUrl(getUrl({ path: "/s" }), { coupon: "GET5WITHBOUNCE" }),
    eventProps: {
      location: eventPropsName,
    },
  })
  const { t } = useTranslation()

  return (
    <>
      {hideBurgerButton && (
        <SafeLink href={href} onClick={onClick} className="md:hidden">
          <Button size="medium" variant="secondary" onPress={null}>
            {t("cmp.appBannerTertiary.cta", "Get the app")}
          </Button>
        </SafeLink>
      )}

      {!hideBurgerButton && (
        <NavbarBurgerButton
          label={t("cmp.navBar.menuButtonLabel", "Menu")}
          open={showMenu}
          onClick={onClickBurger}
          className="flex xl:hidden"
        />
      )}
    </>
  )
}
